/**
 * ClientPortalToursListHighlight Hook
 *
 * Handles bidirectional hover highlighting between tour cards and map pins:
 * - Map → List: When a map pin is hovered, highlights the corresponding card and scrolls if needed
 * - List → Map: When a card is hovered, dispatches event to highlight the map pin
 */
export const ClientPortalToursListHighlight = {
  mounted() {
    this.hoveredStopId = null
    this.currentlyHighlightedId = null
    this.setupMapToListListeners()
    this.setupListToMapListeners()
  },

  setupMapToListListeners() {
    this.mapHighlightHandler = (event) => {
      const stopId = String(event.detail.stopId)
      if (this.currentlyHighlightedId === stopId) return
      this.highlightStopCard(stopId, true)
    }

    this.mapUnhighlightHandler = () => {
      this.unhighlightAllStopCards()
    }

    window.addEventListener(
      "tours-list:highlight-stop",
      this.mapHighlightHandler,
    )
    window.addEventListener(
      "tours-list:unhighlight-stop",
      this.mapUnhighlightHandler,
    )
  },

  setupListToMapListeners() {
    this.el.addEventListener("mouseover", (e) => {
      const card = e.target.closest("[data-stop-id]")
      if (!card) return

      const stopId = card.dataset.stopId
      if (this.hoveredStopId === stopId) return

      this.hoveredStopId = stopId
      this.highlightStopCard(stopId, false)
      window.dispatchEvent(
        new CustomEvent("tours-map:highlight-stop", { detail: { stopId } }),
      )
    })

    this.el.addEventListener("mouseout", (e) => {
      const card = e.target.closest("[data-stop-id]")
      if (!card) return

      const toElement = e.relatedTarget
      if (toElement && card.contains(toElement)) return

      this.hoveredStopId = null
      this.unhighlightAllStopCards()
      window.dispatchEvent(
        new CustomEvent("tours-map:unhighlight-stop", {
          detail: { stopId: card.dataset.stopId },
        }),
      )
    })
  },

  highlightStopCard(stopId, shouldScroll) {
    this.currentlyHighlightedId = String(stopId)

    const card = this.el.querySelector(`[data-stop-id="${stopId}"]`)
    if (!card) return

    this.unhighlightAllStopCards()
    card.classList.add("shadow-sm", "bg-white", "-translate-y-[1px]")
    card.classList.remove("bg-gray-50")

    const tourColor = card.dataset.tourColor
    if (tourColor) {
      card.style.borderColor = tourColor
    }

    if (shouldScroll) {
      const cardRect = card.getBoundingClientRect()
      const containerRect = this.el.getBoundingClientRect()
      const isAbove = cardRect.bottom < containerRect.top
      const isBelow = cardRect.top > containerRect.bottom

      if (isAbove || isBelow) {
        card.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  },

  unhighlightAllStopCards() {
    this.currentlyHighlightedId = null
    this.el.querySelectorAll("[data-stop-id]").forEach((card) => {
      card.classList.remove("shadow-sm", "bg-white", "-translate-y-[1px]")
      card.classList.add("bg-gray-50")
      card.style.borderColor = ""
    })
  },

  destroyed() {
    window.removeEventListener(
      "tours-list:highlight-stop",
      this.mapHighlightHandler,
    )
    window.removeEventListener(
      "tours-list:unhighlight-stop",
      this.mapUnhighlightHandler,
    )
  },
}
